/**
 * Ícones da interface (F111, ADR 0024): SVG do Lucide (ISC), desenhados pelo `icon()` com
 * `createElementNS` (nada de `innerHTML`, a CSP fica intacta). Esta lista é o vocabulário de ícones de
 * todas as apps: quem precisa de um ícone novo o acrescenta aqui (e no guia), para o mesmo gesto ter o
 * mesmo desenho em toda parte. Cada app importa só os que usa (o resto sai do bundle). O `Map` sai
 * como `MapIcon` para não esconder o `Map` do JavaScript em quem importa.
 */
import type { IconNode } from "lucide";
import { cx } from "./dom.ts";

export type { IconNode };

export {
  // Ações e navegação
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowDownAZ,
  ArrowUpDown,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  Copy,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Funnel,
  House,
  ListFilter,
  LogIn,
  LogOut,
  Menu,
  Minus,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Send,
  Settings,
  SlidersHorizontal,
  Trash,
  Upload,
  X,
  // Retorno
  CircleAlert,
  CircleCheck,
  CircleQuestionMark,
  CircleX,
  Info,
  Loader,
  TriangleAlert,
  // Preferências e conta
  Keyboard,
  Languages,
  LayoutDashboard,
  Mail,
  Moon,
  Palette,
  Sun,
  User,
  Users,
  // Reprodução, câmera e mouse (animador)
  Bone,
  Box,
  Camera,
  Eye,
  EyeOff,
  Focus,
  Grid3x3,
  Move,
  Move3d,
  Mouse,
  MouseLeft,
  MouseRight,
  Pause,
  Play,
  Redo2,
  Repeat,
  Rotate3d,
  Scan,
  SkipBack,
  SkipForward,
  StepBack,
  StepForward,
  Undo2,
  ZoomIn,
  ZoomOut,
  // Jogo: vitais, painéis e categorias
  Activity,
  ArrowDownToLine,
  Award,
  Backpack,
  BrickWall,
  Coins,
  Compass,
  Crosshair,
  Drumstick,
  Footprints,
  FlaskConical,
  Gamepad2,
  Gauge,
  Gem,
  Globe,
  Hammer,
  Hand,
  Heart,
  IdCard,
  KeyRound,
  Layers,
  LayoutGrid,
  List,
  LocateFixed,
  Map as MapIcon,
  MapPin,
  MessageSquare,
  MousePointerClick,
  Monitor,
  Package,
  Pickaxe,
  ScrollText,
  Shield,
  Skull,
  Split,
  Sparkles,
  Star,
  Sword,
  Swords,
  Target,
  Trophy,
  Volume2,
  Wind,
  Zap,
} from "lucide";

export interface IconOptions {
  /** Nome acessível. Sem ele, o ícone é decorativo (`aria-hidden`), e o texto ao lado diz o que é. */
  label?: string | undefined;
  class?: string | undefined;
  /** Espessura do traço (o padrão do Lucide é 2). */
  strokeWidth?: number | undefined;
}

const SVG = "http://www.w3.org/2000/svg";

/** Desenha um ícone do Lucide como `<svg class="icon">` na cor do texto (`currentColor`). */
export function icon(node: IconNode, options: IconOptions = {}): SVGSVGElement {
  const svg = document.createElementNS(SVG, "svg");
  const attrs: Record<string, string> = {
    xmlns: SVG,
    viewBox: "0 0 24 24",
    width: "24",
    height: "24",
    fill: "none",
    stroke: "currentColor",
    "stroke-width": String(options.strokeWidth ?? 2),
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    class: cx("icon", options.class),
    focusable: "false",
  };
  for (const [k, v] of Object.entries(attrs)) svg.setAttribute(k, v);
  if (options.label) {
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", options.label);
  } else svg.setAttribute("aria-hidden", "true");
  for (const [tag, props] of node) {
    const child = document.createElementNS(SVG, tag);
    for (const [k, v] of Object.entries(props)) if (v !== undefined) child.setAttribute(k, String(v));
    svg.append(child);
  }
  return svg;
}
